import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  FileIcon,
  FileText,
  Image as ImageIcon,
  ExternalLink,
  X,
  Download,
  Maximize2,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { FileData } from './document-upload';
import { PDFViewer } from './pdf-viewer';

// Helper function to try to parse JSON string
function tryParseJSON(jsonString: string): any | null {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Failed to parse JSON:', e);
    return null;
  }
}

interface DocumentViewerProps {
  documents: FileData[];
  title: string;
  emptyMessage?: string;
}

export function DocumentViewer({
  documents,
  title,
  emptyMessage = 'No documents uploaded',
}: DocumentViewerProps) {
  // State for PDF viewer dialog
  const [selectedPdf, setSelectedPdf] = useState<FileData | null>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  // Extract filename from embedded data URL
  const extractFilenameFromDataUrl = (dataUrl: string): string | null => {
    if (!dataUrl || typeof dataUrl !== 'string') {
      return null;
    }

    const filenameMatch = dataUrl.match(/;filename=([^;]+);/);
    if (filenameMatch && filenameMatch[1]) {
      try {
        return decodeURIComponent(filenameMatch[1]);
      } catch (e) {
        console.error('Failed to decode filename:', e);
      }
    }
    return null;
  };

  // Handle legacy string array format for backward compatibility
  const normalizedDocuments = React.useMemo(() => {
    if (!documents || documents.length === 0) return [];

    // For debugging document visibility
    console.log('Raw documents data:', documents);

    // First, filter out null, undefined, or empty string values
    const filteredDocs = documents.filter((doc: any) => {
      if (doc === null || doc === undefined || doc === '') {
        console.log('Filtering out null/undefined/empty document');
        return false;
      }

      // If it's an object, check if it has essential data (id, data, or filePath)
      if (typeof doc === 'object') {
        // For deleted files or files that have been marked as deleted
        if (doc.deleted || doc.isDeleted) {
          console.log(`Filtering out deleted document:`, doc.id || doc.filename);
          return false;
        }

        // If the object has no usable data (content or URL)
        const hasUsableData = Boolean(
          doc.data || doc.id || doc.filePath || doc.filename || doc.url
        );

        if (!hasUsableData) {
          console.log('Filtering out document with no usable data properties');
          return false;
        }
      }

      // Keep all other entries
      return true;
    });

    return filteredDocs
      .map((doc: any, index: number) => {
        // Debug each document
        console.log(`Processing document [${index}]:`, doc);

        // If it's already in the correct format (FileData object)
        if (typeof doc === 'object') {
          // Normalize the document object to ensure it has the expected properties
          const normalizedDoc: FileData = {
            // Use name property, falling back to originalFilename, then filename if needed
            name: doc.name || doc.originalFilename || doc.filename || 'Unknown document',
            // Prioritize filePath if available (this is the correct direct link)
            data:
              doc.filePath ||
              doc.data ||
              doc.url ||
              (doc.id ? `/api/documents/${doc.id}` : undefined),
            // Use type property, falling back to mimeType
            type: doc.type || doc.mimeType || 'application/octet-stream',
            // Pass through the ID
            id: doc.id,
            // Use size property, falling back to fileSize
            size: doc.size || doc.fileSize || 0,
            // Preserve filePath for checking
            filePath: doc.filePath,
          };

          // If still no data URL but we have filePath, construct URL from filePath
          if (!normalizedDoc.data && doc.filePath) {
            const filename = doc.filePath.split('/').pop() || normalizedDoc.name;
            console.log(`Document has filePath but no data URL, using filename: ${filename}`);
            normalizedDoc.data = `/api/files/${encodeURIComponent(filename)}`;
            // If no ID yet, use filename as ID
            if (!normalizedDoc.id) {
              normalizedDoc.id = filename;
            }
          }

          // If still no data URL but we have filename, construct URL from filename
          if (!normalizedDoc.data && doc.filename) {
            console.log(`Document has filename but no data URL: ${doc.filename}`);
            normalizedDoc.data = `/api/files/${encodeURIComponent(doc.filename)}`;
            // If no ID yet, use filename as ID
            if (!normalizedDoc.id) {
              normalizedDoc.id = doc.filename;
            }
          }

          // Fix any non-string name (rare case but happens with some older entries)
          if (normalizedDoc.name && typeof normalizedDoc.name !== 'string') {
            console.warn('Document has non-string name, converting to string', normalizedDoc.name);
            normalizedDoc.name = String(normalizedDoc.name);
          }

          console.log(`Normalized document:`, normalizedDoc);
          return normalizedDoc;
        }

        // If it's a JSON string representing a FileData object
        if (typeof doc === 'string' && doc.startsWith('{') && doc.endsWith('}')) {
          const parsedDoc = tryParseJSON(doc);
          if (parsedDoc) {
            console.log('Successfully parsed serialized file data:', parsedDoc.name || 'unnamed');

            // Ensure we have a data URL
            if (!parsedDoc.data && parsedDoc.id) {
              parsedDoc.data = `/api/documents/${parsedDoc.id}`;
            }

            return parsedDoc as FileData;
          }
        }

        // If it's a string (base64 data URL)
        if (typeof doc === 'string' && doc.startsWith('data:')) {
          // Extract file type from data URL
          const typeMatch = doc.match(/^data:([^;]+);/);
          const fileType = typeMatch ? typeMatch[1] : 'application/octet-stream';

          // Try to extract embedded filename from data URL (our new format)
          let fileName = extractFilenameFromDataUrl(doc);

          // If no embedded filename, create a synthetic one
          if (!fileName) {
            const ext = fileType.split('/')[1] || 'unknown';
            const fileDate = new Date().toISOString().split('T')[0];
            fileName = `Document_${fileDate}.${ext}`;
          }

          return {
            data: doc,
            name: fileName,
            type: fileType,
            size: Math.round(doc.length * 0.75), // Rough estimate of decoded size
          };
        }

        // If it's just a plain path string (common in the file IDs)
        if (typeof doc === 'string' && !doc.startsWith('{') && !doc.startsWith('data:')) {
          // Handle LOA references specially
          if (doc.startsWith('loa_') || doc.startsWith('LOA-')) {
            const loaId = doc.replace('loa_', '').replace('LOA-', '');
            console.log(`Found LOA reference: ${doc}, linking to LOA ${loaId}`);

            return {
              data: `/loa-detail/${loaId}`,
              name: `Letter of Authority ${loaId}`,
              type: 'application/pdf',
              id: doc,
              size: 0,
              isLOA: true,
            };
          }

          // If it looks like a file path, use it directly
          if (doc.includes('/') || doc.includes('.')) {
            const fileName = doc.split('/').pop() || 'document';
            console.log(`Found path string: ${doc}, using as direct file URL`);

            // Determine file type from extension
            let fileType = 'application/octet-stream';
            if (fileName.endsWith('.pdf')) fileType = 'application/pdf';
            else if (fileName.endsWith('.png')) fileType = 'image/png';
            else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg'))
              fileType = 'image/jpeg';
            else if (fileName.endsWith('.svg')) fileType = 'image/svg+xml';

            return {
              data: doc.startsWith('/') ? doc : `/api/files/${doc}`,
              name: fileName,
              type: fileType,
              id: doc,
              size: 0,
            };
          }
        }

        // Fallback for any other unexpected format
        console.warn('Document in unexpected format:', doc);
        return {
          data: '',
          name: 'Unknown document',
          type: 'application/octet-stream',
          size: 0,
        };
      })
      .filter((doc: any) => doc.data); // Filter out any invalid documents
  }, [documents]);

  // Get appropriate icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon className="h-5 w-5 shrink-0 text-primary" />;
    } else if (fileType.includes('pdf')) {
      return <FileText className="h-5 w-5 shrink-0 text-primary" />;
    } else {
      return <FileIcon className="h-5 w-5 shrink-0 text-primary" />;
    }
  };

  // View file handler with improved error handling
  const viewFile = (file: FileData) => {
    console.log('Viewing file:', file);
    console.log('File filePath:', (file as any).filePath);
    console.log('File data:', file.data);

    // Check if this is an LOA document by looking at multiple indicators
    const isLoaDocument =
      (file as any).filePath?.includes('/letters-of-authority/') ||
      (file as any).filePath?.includes('/loa/') ||
      file.data?.includes('/letters-of-authority/') ||
      file.data?.includes('/loa/') ||
      file.data?.startsWith('/loa/') ||
      (file as any).isLOA ||
      (file as any).loaId ||
      file.id?.toString().startsWith('loa-') ||
      file.id?.toString().startsWith('loa_') ||
      file.name?.toLowerCase().includes('letter_of_authority') ||
      file.name?.toLowerCase().includes('letter of authority') ||
      (file as any).filename?.toLowerCase().includes('letter_of_authority') ||
      (file as any).filename?.toLowerCase().includes('letter of authority') ||
      (file as any).filename?.includes('Letter_of_Authority_') ||
      (file as any).category === 'loa';

    if (isLoaDocument) {
      // Extract LOA ID from various possible sources
      let loaId = '';

      // Direct loaId property (most reliable)
      if ((file as any).loaId) {
        loaId = (file as any).loaId.toString();
        console.log(`Found loaId property: ${loaId}`);
      }
      // ID starting with loa-
      else if (file.id?.toString().startsWith('loa-')) {
        loaId = file.id.toString().replace('loa-', '');
        console.log(`Found loa- ID: ${loaId}`);
      }
      // ID starting with loa_
      else if (file.id?.toString().startsWith('loa_')) {
        loaId = file.id.toString().replace('loa_', '');
        console.log(`Found loa_ ID: ${loaId}`);
      }
      // Extract from filePath like /loa/123
      else if ((file as any).filePath?.includes('/loa/')) {
        const match = (file as any).filePath.match(/\/loa\/(\d+)/);
        if (match) {
          loaId = match[1];
          console.log(`Found LOA ID from /loa/ filePath: ${loaId}`);
        }
      }
      // Extract from data URL like /loa/123
      else if (file.data?.includes('/loa/') || file.data?.startsWith('/loa/')) {
        const match = file.data.match(/\/loa\/(\d+)/);
        if (match) {
          loaId = match[1];
          console.log(`Found LOA ID from /loa/ data URL: ${loaId}`);
        }
      }
      // Extract from letters-of-authority path
      else if (file.data?.includes('/letters-of-authority/')) {
        const match = file.data.match(/\/letters-of-authority\/(\d+)/);
        if (match) {
          loaId = match[1];
          console.log(`Found LOA ID from letters-of-authority path: ${loaId}`);
        }
      }
      // Extract from filePath like /letters-of-authority/123
      else if ((file as any).filePath?.includes('/letters-of-authority/')) {
        const match = (file as any).filePath.match(/\/letters-of-authority\/(\d+)/);
        if (match) {
          loaId = match[1];
          console.log(`Found LOA ID from letters-of-authority filePath: ${loaId}`);
        }
      }
      // Extract from filename pattern Letter_of_Authority_123
      else if ((file as any).filename?.includes('Letter_of_Authority_')) {
        const match = (file as any).filename.match(/Letter_of_Authority_(\d+)/);
        if (match) {
          loaId = match[1];
          console.log(`Found LOA ID from filename pattern: ${loaId}`);
        }
      }
      // Extract from name pattern
      else if (file.name?.includes('Letter of Authority')) {
        // Try multiple patterns for LOA name
        const patterns = [
          /Letter of Authority.*?-\s*(\d+)/i, // "Letter of Authority - 123"
          /Letter of Authority.*?(\d+)/i, // "Letter of Authority 123"
          /LOA.*?(\d+)/i, // "LOA 123"
          /Authority.*?(\d+)/i, // "Authority 123"
        ];
        for (const pattern of patterns) {
          const match = file.name.match(pattern);
          if (match) {
            loaId = match[1];
            console.log(`Found LOA ID from name pattern: ${loaId}`);
            break;
          }
        }
      }

      if (loaId) {
        console.log('Opening LOA document with ID:', loaId);
        // Use navigate instead of window.open for consistency with the installation detail component
        window.location.href = `/loa/${loaId}`;
        return;
      } else {
        console.warn('Could not extract LOA ID from document:', file);
        console.warn('Available properties:', Object.keys(file));
      }
    }

    // Extract document ID for routing
    let documentId: string | number | null = null;

    // Determine document ID from either direct ID or from URL
    if (file.id) {
      // Direct ID might be a number or string
      documentId = file.id;
      console.log(`Using explicit document ID: ${documentId} (type: ${typeof documentId})`);
    } else if (file.data && file.data.startsWith('/api/documents/')) {
      // Extract ID from URL path
      const idMatch = file.data.match(/\/api\/documents\/(\d+)/);
      if (idMatch && idMatch[1]) {
        documentId = parseInt(idMatch[1], 10);
        console.log(`Extracted document ID from URL: ${documentId}`);
      }
    }

    // Handle PDF files - use direct document links instead of fallback viewer
    if (file.type === 'application/pdf') {
      // Check if this document has a direct filePath (like Key Documents do)
      if ((file as any).filePath) {
        console.log(`Opening PDF via direct filePath: ${(file as any).filePath}`);
        window.open((file as any).filePath, '_blank');
        return;
      }

      // For documents with ID, use the direct document API endpoint
      if (documentId) {
        console.log(`Opening PDF via document API: Document ID ${documentId}`);
        window.open(`/api/documents/${documentId}`, '_blank');
        return;
      }

      // For PDFs with data URL that starts with /api/, open directly
      if (file.data && file.data.startsWith('/api/')) {
        console.log('Opening PDF via direct API URL:', file.data);
        window.open(file.data, '_blank');
        return;
      }

      // For PDFs with data URL, use embedded viewer
      if (file.data) {
        console.log('Opening PDF in embedded viewer');
        setSelectedPdf(file);
        setPdfViewerOpen(true);
        return;
      }
    }

    // For server-stored files (any type)
    if (
      file.data &&
      (file.data.startsWith('/api/files/') ||
        file.data.startsWith('/api/documents/') ||
        file.data.includes('/api/'))
    ) {
      console.log('Opening server file URL:', file.data);

      // Use direct API endpoints for all file types - no fallback processing
      if (documentId) {
        console.log(`Opening document via direct API: /api/documents/${documentId}?download=true`);
        // Force download for non-PDF files to avoid fallback generation
        if (file.type !== 'application/pdf') {
          window.open(`/api/documents/${documentId}?download=true`, '_blank');
        } else {
          window.open(`/api/documents/${documentId}`, '_blank');
        }
        return;
      }

      // For old /api/files/ URLs, convert to direct document API
      let fileUrl = file.data;
      if (fileUrl.startsWith('/api/files/')) {
        const fileIdStr = file.id !== undefined && file.id !== null ? String(file.id) : '';
        const fileId = fileIdStr
          ? isNaN(parseInt(fileIdStr))
            ? fileIdStr
            : parseInt(fileIdStr)
          : '';
        if (typeof fileId === 'number') {
          // Use direct document API for all file types
          fileUrl = `/api/documents/${fileId}`;
          console.log('Converting to direct document API URL:', fileUrl);
          window.open(fileUrl, '_blank');
          return;
        }
      }

      // Handle special cases for certificate PDFs that might have encoding issues
      if (
        file.type === 'application/pdf' &&
        (file.name.toLowerCase().includes('certificate') ||
          file.name.toLowerCase().includes('certification'))
      ) {
        // For certificates, try to ensure fallback option is available by appending parameter
        const url = `${file.data}${file.data.includes('?') ? '&' : '?'}fallback=true`;
        console.log('Opening certificate with fallback support:', url);
        window.open(url, '_blank');
        return;
      }

      // If no special handling needed, open the URL directly
      window.open(file.data, '_blank');
      return;
    }

    // For legacy data URLs, use the standard approach to open in a new tab
    window.open(file.data, '_blank');
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{title}</h3>

      {normalizedDocuments.length === 0 ? (
        <p className="text-muted-foreground text-sm">{emptyMessage}</p>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {normalizedDocuments.map((doc, index) => (
            <div
              key={index}
              className="border rounded-md overflow-hidden bg-white dark:bg-slate-900"
            >
              {/* Preview for images */}
              {doc.type.startsWith('image/') && (
                <div className="w-full h-36 overflow-hidden bg-slate-50 dark:bg-slate-800 border-b">
                  <img
                    src={doc.data}
                    alt={doc.name}
                    className="w-full h-full object-contain"
                    onClick={() => viewFile(doc)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              )}

              {/* Enhanced Preview for PDFs - Improved direct viewing */}
              {doc.type === 'application/pdf' && (
                <div className="w-full h-52 overflow-hidden bg-slate-50 dark:bg-slate-800 border-b">
                  <div className="relative w-full h-full">
                    {/* PDF preview with fallback display - always use clickable preview for PDFs */}
                    {/* Always use clickable preview instead of iframe for PDFs */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4 bg-slate-100 dark:bg-slate-800">
                      <FileText className="w-16 h-16 text-primary/70 mb-3" />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{doc.name}</p>
                      <div className="flex gap-2">
                        <Button onClick={() => viewFile(doc)} variant="outline" size="sm">
                          <Maximize2 className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview for other file types */}
              {!doc.type.startsWith('image/') && doc.type !== 'application/pdf' && (
                <div className="w-full h-40 overflow-hidden bg-slate-50 dark:bg-slate-800 border-b">
                  <div className="flex flex-col items-center justify-center h-full p-4">
                    {getFileIcon(doc.type)}
                    <p className="mt-2 text-sm text-center text-muted-foreground">{doc.name}</p>
                  </div>
                </div>
              )}

              {/* File info and actions */}
              <div className="p-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-2 grow truncate">
                    {getFileIcon(doc.type)}
                    <div className="truncate">
                      <h4 className="font-medium text-sm truncate">{doc.name}</h4>
                      <p className="text-xs text-muted-foreground">{formatFileSize(doc.size)}</p>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => viewFile(doc)}
                    className="shrink-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="sr-only">View</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PDF Viewer modal */}
      {pdfViewerOpen && selectedPdf && (
        <PDFViewer
          pdfData={selectedPdf.data}
          fileName={selectedPdf.name}
          onClose={() => setPdfViewerOpen(false)}
        />
      )}
    </div>
  );
}
